/**
 * Splitting ONE analysis-PDF section across pages.
 *
 * The section packer in bloodwork-analysis-pdf.tsx decides which sections share
 * a page. This decides what happens when a single section is taller than a page
 * on its own, which the packer cannot help with: a practitioner's urinalysis is
 * four bar graphs and thirty-four text results, about 1,700px against a 900px
 * page, and html2pdf answers that by carrying the overflow onto the next page
 * with no title and no card header above it.
 *
 * So the section is pre-paginated here instead, the same way Nutrient Status
 * already splits at its marker count and the comparison tables split at
 * their rows: each chunk is a page's worth, rendered as its own card, under a
 * heading that repeats as "Urinalysis (cont.)".
 *
 * Pure arithmetic, so it can be tested without a browser.
 */

export type SectionRowChunk = {
  /** Bar-graph markers on this chunk, as slice bounds. */
  markerStart: number;
  markerEnd: number;
  /** Text results on this chunk, as slice bounds. */
  textStart: number;
  textEnd: number;
};

export type SectionRowMetrics = {
  /** Usable height of a page. */
  capacity: number;
  /** Title plus card chrome, paid once per CHUNK — each one is its own card. */
  baseHeight: number;
  /** One bar graph. */
  markerRowHeight: number;
  /** The rule above and below a block of text results, once per chunk. */
  textBlockHeight: number;
  /**
   * The most bar graphs one card carries, if the section is to cap them.
   *
   * Nutrient Status and Hormone Balance have split their charts at a
   * fixed count for years, and that count is this one; the sections a
   * practitioner builds had no cap at all, so a panel of twenty of their own
   * markers printed a card taller than the page with nothing over the overflow.
   */
  maxMarkersPerChunk?: number;
};

/**
 * How a section's rows divide into pages.
 *
 * `textRowHeights` is one entry per text result, because they are not all the
 * same height: a row that prints the previous collection's result underneath is
 * a line taller, and a urinalysis of thirty-four rows is half a page longer
 * when they all do.
 *
 * THE CHARTS COME FIRST AND FILL WHOLE CARDS, capped at
 * `maxMarkersPerChunk` where the caller sets one; the text results start once
 * the last chart is placed, and take the room left on that card before opening
 * another. That is the order the card prints them in, so a split lands where
 * the page runs out rather than always between the two kinds.
 *
 * The cap is how Nutrient Status and Hormone Balance have split their
 * charts for years. It reads the same number here so there is one answer rather
 * than two, and so the sections a practitioner builds — which had no cap and no
 * ceiling on how many markers they hold — split the same way.
 *
 * Always returns at least one chunk, so a section is never dropped for having
 * nothing that fits.
 */
export function packSectionRows(
  markerCount: number,
  textRowHeights: number[],
  metrics: SectionRowMetrics,
): SectionRowChunk[] {
  const markers = Math.max(0, Math.floor(markerCount) || 0);
  const texts = textRowHeights.length;
  const {
    capacity,
    baseHeight,
    markerRowHeight,
    textBlockHeight,
    maxMarkersPerChunk,
  } = metrics;
  const markerCap = Math.max(
    1,
    Math.floor(maxMarkersPerChunk || 0) || Number.MAX_SAFE_INTEGER,
  );

  const chunks: SectionRowChunk[] = [];
  let markersTaken = 0;
  let taken = 0;

  while (markersTaken < markers || taken < texts || !chunks.length) {
    // The charts come first, in the order the card prints them, capped so a
    // section of twenty of them is two cards rather than one off the page.
    const markersHere = Math.min(markerCap, markers - markersTaken);
    let spent = baseHeight + markersHere * markerRowHeight;
    // Text results begin only once the charts are all placed: a card of
    // charts-continued followed by words would read as two sections.
    const canTakeText = markersTaken + markersHere >= markers && taken < texts;
    if (canTakeText) {
      spent += textBlockHeight;
    }
    let end = taken;
    if (canTakeText) {
      while (end < texts) {
        const rowHeight = textRowHeights[end];
        // A card has to carry SOMETHING or the loop cannot advance — but a card
        // already carrying charts is carrying something, and forcing a row onto
        // one the charts have already filled is what printed a card 36px past
        // the page it was cut for.
        if (spent + rowHeight > capacity && (end > taken || markersHere > 0)) {
          break;
        }
        spent += rowHeight;
        end += 1;
      }
    }
    chunks.push({
      markerStart: markersTaken,
      markerEnd: markersTaken + markersHere,
      textStart: taken,
      textEnd: end,
    });
    const advanced = markersHere > 0 || end > taken;
    markersTaken += markersHere;
    taken = end;
    if (!advanced) {
      break;
    }
  }

  return chunks;
}

/** True when a section needs more than one page. */
export function sectionNeedsRowSplit(
  markerCount: number,
  textRowHeights: number[],
  metrics: SectionRowMetrics,
): boolean {
  return packSectionRows(markerCount, textRowHeights, metrics).length > 1;
}

/**
 * What one chunk costs a page, for the section packer's unit heights.
 *
 * The same arithmetic packSectionRows fills with, so the two cannot disagree
 * about what it just decided.
 */
export function sectionChunkHeight(
  chunk: SectionRowChunk,
  textRowHeights: number[],
  metrics: SectionRowMetrics,
): number {
  const markers = chunk.markerEnd - chunk.markerStart;
  const rows = textRowHeights.slice(chunk.textStart, chunk.textEnd);
  return (
    metrics.baseHeight
    + markers * metrics.markerRowHeight
    + (rows.length ? metrics.textBlockHeight : 0)
    + rows.reduce((total, height) => total + height, 0)
  );
}
